import * as React from 'react';
import type { StyleProp, ViewStyle } from 'react-native';

import type { ScreenType } from '@gitbook/spine-react';
import { filterOutNullable, type MandateProps } from '@gitbook/util-ts';

import { spacing } from '../constants';
import { Responsive, type ResponsiveViewDefaultStyles } from '../Responsive';

export interface GridListRenderItemInfo<Item> {
    item: Item;
    index: number;
}

export interface GridListRow<Item> {
    data: Item[];
    index: number;
}

export interface GridListColumnDelimiterProps<Item> {
    previousItem?: GridListRenderItemInfo<Item>;
    nextItem?: GridListRenderItemInfo<Item>;
}

export interface GridListRowDelimiterProps<Item> {
    previousItem?: GridListRenderItemInfo<Item>;
    nextItem?: GridListRenderItemInfo<Item>;
}

export interface GridListProps<Item> {
    /**
     * Number of columns per screen type for this grid. Will responsively resize itself when
     * the screen type changes.
     */
    numColumnsPerScreen: Record<ScreenType, number>;

    /**
     * Items to render in the grid.
     */
    data: Item[];

    columnSpacing?: number;
    rowSpacing?: number;

    style?: StyleProp<ViewStyle>;

    /** Takes an item from data and renders it into the list. */
    renderItem: (info: GridListRenderItemInfo<Item>) => React.ReactNode;

    /** Used to extract a unique key for a given item at the specified index. */
    keyExtractor?: (item: Item, index: number) => string;

    /** Component to render between each rows */
    RowDelimiterComponent?: React.ComponentType<GridListRowDelimiterProps<Item>>;

    /** Component to render between each columns */
    ColumnDelimiterComponent?: React.ComponentType<GridListColumnDelimiterProps<Item>>;
}

/**
 * Render a grid of elements, similar to FlatList.
 * It uses CSS grid to optimize the layout.
 */
export function GridList<Item>(props: GridListProps<Item>): React.ReactElement {
    const {
        data,
        numColumnsPerScreen,
        renderItem,
        style,
        columnSpacing = spacing.small,
        rowSpacing = spacing.small,
        keyExtractor = defaultKeyExtractor,
        RowDelimiterComponent,
        ColumnDelimiterComponent,
    } = props;

    const withRowsDelimiter = !!RowDelimiterComponent;
    const withColumnsDelimiter = !!ColumnDelimiterComponent;

    const styleProps = React.useMemo(() => {
        return computeGridStyle(
            data.length,
            numColumnsPerScreen,
            rowSpacing,
            columnSpacing,
            withRowsDelimiter,
            withColumnsDelimiter
        );
    }, [
        data.length,
        numColumnsPerScreen,
        rowSpacing,
        columnSpacing,
        withRowsDelimiter,
        withColumnsDelimiter,
    ]);

    // PERF: To avoid computing the styles for all elements, we only compute if for the elements of the first row
    const itemsStyles = React.useMemo(() => {
        return computeGridItemsStyle(numColumnsPerScreen, withRowsDelimiter, withColumnsDelimiter);
    }, [numColumnsPerScreen, withRowsDelimiter, withColumnsDelimiter]);

    return (
        <Responsive.View style={[style]} {...styleProps}>
            {data.map((item, index) => {
                const currentItem = { item, index };
                const nextItem =
                    index < data.length ? { item: data[index + 1], index: index + 1 } : undefined;

                return (
                    <React.Fragment key={keyExtractor(item, index)}>
                        <Responsive.View {...extractResponsiveStyles(index, itemsStyles, 'style')}>
                            {renderItem(currentItem)}
                        </Responsive.View>
                        {ColumnDelimiterComponent ? (
                            <Responsive.View
                                {...extractResponsiveStyles(
                                    index,
                                    itemsStyles,
                                    'columnDelimiterStyle'
                                )}
                            >
                                <ColumnDelimiterComponent
                                    previousItem={currentItem}
                                    nextItem={nextItem}
                                />
                            </Responsive.View>
                        ) : null}
                        {RowDelimiterComponent ? (
                            <Responsive.View
                                {...extractResponsiveStyles(
                                    index,
                                    itemsStyles,
                                    'rowDelimiterStyle'
                                )}
                            >
                                <RowDelimiterComponent
                                    previousItem={currentItem}
                                    nextItem={nextItem}
                                />
                            </Responsive.View>
                        ) : null}
                    </React.Fragment>
                );
            })}
        </Responsive.View>
    );
}

function defaultKeyExtractor(item) {
    return item.key;
}

/**
 * Compute the CSS grid templates to use for this grid.
 */
function computeGridStyle(
    numElements: number,
    numColumnsPerScreen: Record<ScreenType, number>,
    rowSpacing: number,
    columnSpacing: number,
    withRowsDelimiter: boolean,
    withColumnsDelimiter: boolean
) {
    const styles: ResponsiveViewDefaultStyles = {};

    for (const screenType of Object.keys(numColumnsPerScreen) as ScreenType[]) {
        const styleProp = `${screenType}Style` as keyof typeof styles;
        const numColumns = numColumnsPerScreen[screenType];
        const numRows = Math.ceil(numElements / numColumns);

        styles[styleProp] = {
            // @ts-ignore
            display: 'grid',
            // When using delimiter we need to take the last delimiter into account
            marginRight: withColumnsDelimiter ? -columnSpacing : 0,
            // @ts-ignore
            gap: `${withRowsDelimiter ? 0 : rowSpacing}px ${
                withColumnsDelimiter ? 0 : columnSpacing
            }px`,
            ...(withRowsDelimiter
                ? {
                      gridTemplateRows: `repeat(${numRows}, 1fr ${
                          withRowsDelimiter ? rowSpacing : 0
                      }px)`,
                  }
                : {}),
            // @ts-ignore
            gridTemplateColumns: `repeat(${numColumns}, ${[
                '1fr',
                withColumnsDelimiter ? `${columnSpacing}px` : null,
                withRowsDelimiter ? 'auto' : null,
            ]
                .filter(filterOutNullable)
                .join(' ')})`,
        };
    }

    return styles;
}

type GridInternalItemsStyles = {
    [key in keyof MandateProps<
        ResponsiveViewDefaultStyles,
        keyof ResponsiveViewDefaultStyles
    >]: Array<{
        style: ViewStyle;
        columnDelimiterStyle: ViewStyle;
        rowDelimiterStyle: ViewStyle;
    }>;
};

/**
 * Compute the CSS properties for the first N_COLUMNS elements of the grid.
 */
function computeGridItemsStyle(
    numColumnsPerScreen: Record<ScreenType, number>,
    withRowsDelimiter: boolean,
    withColumnsDelimiter: boolean
) {
    const itemStyles: GridInternalItemsStyles = {
        mobileStyle: [],
        tabletStyle: [],
        desktopStyle: [],
    };

    for (const screenType of Object.keys(numColumnsPerScreen) as ScreenType[]) {
        const numColumns = numColumnsPerScreen[screenType];

        for (let index = 0; index < numColumns; index++) {
            const styleProp = `${screenType}Style` as keyof ResponsiveViewDefaultStyles;
            const styles = computeGridItemStyleForScreen(
                index,
                numColumns,
                withRowsDelimiter,
                withColumnsDelimiter
            );

            itemStyles[styleProp][index] = styles;
        }
    }

    return itemStyles;
}

/**
 * Extract the responsive styles for a given index.
 */
function extractResponsiveStyles(
    index: number,
    itemStyles: GridInternalItemsStyles,
    prop: 'style' | 'columnDelimiterStyle' | 'rowDelimiterStyle'
): ResponsiveViewDefaultStyles {
    return {
        mobileStyle: itemStyles.mobileStyle[index % itemStyles.mobileStyle.length][prop],
        tabletStyle: itemStyles.tabletStyle[index % itemStyles.tabletStyle.length][prop],
        desktopStyle: itemStyles.desktopStyle[index % itemStyles.desktopStyle.length][prop],
    };
}

/**
 * Compute the style for a grid item in a specific screen.
 */
function computeGridItemStyleForScreen(
    itemIndex: number,
    numColumns: number,
    withRowsDelimiter: boolean,
    withColumnsDelimiter: boolean
) {
    let style: ViewStyle = {};
    const columnDelimiterStyle: ViewStyle = {};
    let rowDelimiterStyle: ViewStyle = {};

    const elementsPerItem = 1 + (withColumnsDelimiter ? 1 : 0) + (withRowsDelimiter ? 1 : 0);
    const totalColumns = numColumns * elementsPerItem;

    const itemColumn = itemIndex % numColumns;
    const columnIndex = itemColumn * elementsPerItem;

    style = {
        // @ts-ignore
        gridColumn: `${columnIndex + 1} / span 1`,
    };

    if (withRowsDelimiter) {
        rowDelimiterStyle = {
            display: (itemIndex + 1) % numColumns === 0 ? 'flex' : 'none',
            // @ts-ignore
            gridColumn: `1 / span ${totalColumns}`,
        };
    }

    return { style, columnDelimiterStyle, rowDelimiterStyle };
}
